import { Component, OnInit, ViewChild, Input, ElementRef, OnDestroy, AfterViewInit, NgZone, HostListener } from '@angular/core';
import { MycontentserviceService } from '../../../_service/mycontentservice.service';
import { URLS } from '../../../constants/URLS';
import { Router } from '@angular/router';
import { CustomControlsService } from 'src/app/_service/custom-controls.service';
import { WhiteLabelService } from 'src/app/_service/white-label.service';

@Component({
  selector: 'app-top10',
  templateUrl: './top10.component.html',
  styleUrls: ['./top10.component.css']
})
export class Top10Component implements OnInit, OnDestroy, AfterViewInit {

  @Input() movies: any[] = [];
  @Input() isCarousel: boolean = true; // NEW: Control carousel behavior
  mediaUrl: any = URLS.BASE_URL + '/static/post_videos/';
  imgUrl:string = URLS.imgUrl;
  
  @ViewChild('owlCarousel', { static: false }) owlCarousel: ElementRef;
  @ViewChild('carouselTrack', { static: false }) carouselTrack: ElementRef;
  
  isCarouselEnabled: boolean = false; // NEW: Track actual carousel state
  currentIndex = 0;
  translateX = 0;
  isAnimating = false;
  itemWidth = 0;
  itemsPerView = 1;
  maxIndex = 0;
  showNavigation = true;
  marginRight = 0;
  containerWidth = 0;

  private isDragging = false;
  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private currentY = 0;
  private lastX = 0;
  private lastY = 0;
  private initialTranslateX = 0;
  private startTime = 0;
  private lastTime = 0;
  private velocityX = 0;
  private velocityY = 0;
  private isHorizontalSwipe = false;
  private isVerticalSwipe = false;
  private swipeDecided = false;
  private touchStarted = false;
  private moved = false;
  private minSwipeDistance = 10;
  private snapThreshold = 0.3;
  private resizeObserver?: ResizeObserver;
  private animationFrameId: number = 0;
  
  hoveredMovie: any = null;
  hoveredMovieIndex: number = -1;
  popupStyles = { top: '0px', left: '0px' };
  showPopup = false;
  popupTimeout: any;
  imgLoading: boolean = false;
  
  constructor(
    private mycontentservice: MycontentserviceService,
    private router: Router,
    private toastrService: CustomControlsService,
    private ngZone: NgZone,
    private whitelabelService: WhiteLabelService
  ) { }

  ngOnInit(): void {
    this.updateCarouselView();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.setupCarousel();
    });
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.removeEventListeners();
  }

  private setupCarousel(): void {
    if (this.isCarouselEnabled) {
      this.calculateDimensions();
      this.setupResizeObserver();
      this.setupEventListeners();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateCarouselView();
    requestAnimationFrame(() => {
      if (this.isCarouselEnabled) {
        this.calculateDimensions();
      }
    });
  }

  updateCarouselView() {
    const screenWidth = window.innerWidth;
    const minItemsForCurrentScreen = this.getMinItemsForScreen(screenWidth);
    
    // NEW: Enable carousel only if items exceed minimum and isCarousel is true
    this.isCarouselEnabled = this.movies?.length > minItemsForCurrentScreen && this.isCarousel;
    
    if (this.isCarouselEnabled) {
      this.updateResponsiveSettings();
      requestAnimationFrame(() => {
        this.calculateDimensions();
      });
    }
  }

  private updateResponsiveSettings() {
    const screenWidth = window.innerWidth;
    const responsiveConfig = this.getResponsiveConfigForScreen(screenWidth);
    
    this.itemsPerView = responsiveConfig.items;
    this.marginRight = responsiveConfig.margin || 0;
    this.showNavigation = responsiveConfig.nav !== false && this.customOptions.nav !== false;
  }

  private getResponsiveConfigForScreen(screenWidth: number) {
    const breakpoints = Object.keys(this.customOptions.responsive)
      .map(key => parseInt(key))
      .sort((a, b) => b - a);
    
    for (const breakpoint of breakpoints) {
      if (screenWidth >= breakpoint) {
        return this.customOptions.responsive[breakpoint];
      }
    }
    
    const smallestBreakpoint = Math.min(...breakpoints);
    return this.customOptions.responsive[smallestBreakpoint];
  }

  private calculateDimensions() {
    if (!this.owlCarousel || !this.isCarouselEnabled) return;

    this.containerWidth = this.owlCarousel.nativeElement.offsetWidth;
    const totalItems = this.movies.length;
    
    if (totalItems === 0) return;

    const totalMarginSpace = this.marginRight * (this.itemsPerView - 1);
    const availableSpace = this.containerWidth - totalMarginSpace;
    
    this.itemWidth = Math.floor(availableSpace / this.itemsPerView);
    
    if (this.itemWidth < 50) {
      this.itemWidth = Math.floor(this.containerWidth / this.itemsPerView);
    }
    
    this.maxIndex = Math.max(0, totalItems - this.itemsPerView);

    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
    }
    
    this.updateTransform();
  }

  private updateTransform() {
    const newTranslateX = -(this.currentIndex * (this.itemWidth + this.marginRight));
    this.translateX = newTranslateX;
  }

  private setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined' && this.owlCarousel) {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          if (this.isCarouselEnabled) {
            this.calculateDimensions();
          }
        });
      });
      this.resizeObserver.observe(this.owlCarousel.nativeElement);
    }
  }

  private setupEventListeners() {
    if (this.carouselTrack) {
      const element = this.carouselTrack.nativeElement;
      
      element.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
      element.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      element.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
      element.addEventListener('touchcancel', this.onTouchEnd.bind(this), { passive: false });
      
      element.addEventListener('mousedown', this.onMouseDown.bind(this), { passive: false });
      document.addEventListener('mousemove', this.onMouseMove.bind(this), { passive: false });
      document.addEventListener('mouseup', this.onMouseUp.bind(this), { passive: false });
    }
  }

  private removeEventListeners() {
    if (this.carouselTrack) {
      const element = this.carouselTrack.nativeElement;
      
      element.removeEventListener('touchstart', this.onTouchStart.bind(this));
      element.removeEventListener('touchmove', this.onTouchMove.bind(this));
      element.removeEventListener('touchend', this.onTouchEnd.bind(this));
      element.removeEventListener('touchcancel', this.onTouchEnd.bind(this));
      
      element.removeEventListener('mousedown', this.onMouseDown.bind(this));
      document.removeEventListener('mousemove', this.onMouseMove.bind(this));
      document.removeEventListener('mouseup', this.onMouseUp.bind(this));
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex && !this.isAnimating) {
      this.currentIndex++;
      this.smoothSlide();
    }
  }

  prev() {
    if (this.currentIndex > 0 && !this.isAnimating) {
      this.currentIndex--;
      this.smoothSlide();
    }
  }

  private smoothSlide() {
    this.isAnimating = true;
    this.updateTransform();
    
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  onTouchStart(event: TouchEvent) {
    if (!this.customOptions.touchDrag || !this.isCarouselEnabled) return;
    
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      this.touchStarted = true;
      this.moved = false;
      this.swipeDecided = false;
      this.isHorizontalSwipe = false;
      this.isVerticalSwipe = false;
      
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.currentX = touch.clientX;
      this.currentY = touch.clientY;
      this.lastX = touch.clientX;
      this.lastY = touch.clientY;
      this.initialTranslateX = this.translateX;
      this.startTime = Date.now();
      this.lastTime = this.startTime;
      this.velocityX = 0;
      this.velocityY = 0;
      
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = 0;
      }
    }
  }

  onTouchMove(event: TouchEvent) {
    if (!this.touchStarted || event.touches.length !== 1) return;
    
    const touch = event.touches[0];
    const now = Date.now();
    
    this.currentX = touch.clientX;
    this.currentY = touch.clientY;
    
    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;
    
    if (!this.swipeDecided && (Math.abs(deltaX) > this.minSwipeDistance || Math.abs(deltaY) > this.minSwipeDistance)) {
      this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      this.isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX);
      this.swipeDecided = true;
    }
    
    if (this.swipeDecided && this.isHorizontalSwipe) {
      this.moved = true;
      this.isDragging = true;
      
      if (now - this.lastTime > 16) {
        this.velocityX = (this.currentX - this.lastX) / (now - this.lastTime);
        this.lastX = this.currentX;
        this.lastTime = now;
      }
      
      this.updateCarouselPosition(deltaX);
      
      event.preventDefault();
      event.stopPropagation();
    }
    else if (this.swipeDecided && this.isVerticalSwipe) {
      return;
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (!this.touchStarted) return;
    
    this.touchStarted = false;
    
    if (this.isDragging && this.moved) {
      this.endDrag();
      event.preventDefault();
      event.stopPropagation();
    }
    
    this.resetDragState();
  }

  onMouseDown(event: MouseEvent) {
    if (!this.customOptions.mouseDrag || !this.isCarouselEnabled) return;
    
    this.isDragging = true;
    this.startX = event.clientX;
    this.currentX = event.clientX;
    this.lastX = event.clientX;
    this.initialTranslateX = this.translateX;
    this.startTime = Date.now();
    this.lastTime = this.startTime;
    this.velocityX = 0;
    
    event.preventDefault();
    event.stopPropagation();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    
    const now = Date.now();
    this.currentX = event.clientX;
    
    if (now - this.lastTime > 16) {
      this.velocityX = (this.currentX - this.lastX) / (now - this.lastTime);
      this.lastX = this.currentX;
      this.lastTime = now;
    }
    
    const deltaX = this.currentX - this.startX;
    this.updateCarouselPosition(deltaX);
    
    event.preventDefault();
  }

  onMouseUp(event: MouseEvent) {
    if (!this.isDragging) return;
    
    this.endDrag();
    this.resetDragState();
  }

  private updateCarouselPosition(deltaX: number) {
    let newTranslateX = this.initialTranslateX + deltaX;
    
    const maxTranslateX = 0;
    const minTranslateX = -((this.itemWidth + this.marginRight) * this.maxIndex);
    
    if (newTranslateX > maxTranslateX) {
      newTranslateX = maxTranslateX + (newTranslateX - maxTranslateX) * 0.3;
    } else if (newTranslateX < minTranslateX) {
      newTranslateX = minTranslateX + (newTranslateX - minTranslateX) * 0.3;
    }
    
    this.ngZone.runOutsideAngular(() => {
      this.translateX = newTranslateX;
    });
  }

  private endDrag() {
    this.isDragging = false;
    
    const deltaX = this.currentX - this.startX;
    const duration = Date.now() - this.startTime;
    const itemSize = this.itemWidth + this.marginRight;
    
    const currentPosition = -this.translateX / itemSize;
    
    if (Math.abs(this.velocityX) > 1.0 && duration < 300) {
      const velocityFactor = Math.abs(this.velocityX) * 2;
      const itemsToMove = Math.min(Math.ceil(velocityFactor), 3);
      
      if (this.velocityX > 0) {
        this.currentIndex = Math.max(0, Math.floor(currentPosition) - itemsToMove + 1);
      } else {
        this.currentIndex = Math.min(this.maxIndex, Math.floor(currentPosition) + itemsToMove);
      }
    } else {
      this.currentIndex = Math.round(currentPosition);
    }
    
    this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.maxIndex));
    
    this.animateToPosition();
  }

  private animateToPosition() {
    const targetX = -(this.currentIndex * (this.itemWidth + this.marginRight));
    this.isAnimating = true;
    this.translateX = targetX;
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  private resetDragState() {
    this.isDragging = false;
    this.touchStarted = false;
    this.moved = false;
    this.swipeDecided = false;
    this.isHorizontalSwipe = false;
    this.isVerticalSwipe = false;
    this.velocityX = 0;
    this.velocityY = 0;
  }

  getMinItemsForScreen(screenWidth: number): number {
    const breakpoints = Object.keys(this.customOptions.responsive).map(key => parseInt(key)).sort((a, b) => b - a); 
    for (const breakpoint of breakpoints) {
      if (screenWidth >= breakpoint) {
        return this.customOptions.responsive[breakpoint].items;
      }
    }
    const smallestBreakpoint = Math.min(...breakpoints);
    return this.customOptions.responsive[smallestBreakpoint].items;
  }

  onMouseEnter(movie: any, event: MouseEvent, index: number) {
    if (this.isDragging) return;
    
    clearTimeout(this.popupTimeout);
    this.popupTimeout = setTimeout(() => {
      this.hoveredMovie = movie;
      this.hoveredMovieIndex = index;
      this.imgLoading = true;
      
      const targetElement = (event.target as HTMLElement).closest('.top10-item') as HTMLElement;
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const popupWidth = window.innerWidth >= 1400 ? 420 : 380;
        
        let targetX = rect.left;
        let targetY = rect.top;
        
        const thumbnailCenter = rect.left + (rect.width / 2);
        const idealLeft = thumbnailCenter - (popupWidth / 2);
        
        if (idealLeft < 20) {
          targetX = 20;
        } else if (idealLeft + popupWidth > viewportWidth - 20) {
          targetX = viewportWidth - popupWidth - 20;
        } else {
          targetX = idealLeft;
        }
        
        targetY = targetY - 30;
        
        this.popupStyles.left = `${window.scrollX + targetX}px`;
        this.popupStyles.top = `${window.scrollY + targetY}px`;
        
        this.showPopup = true;
      }
    }, 280);
  }

  onMouseLeave(event: MouseEvent) {
    clearTimeout(this.popupTimeout);
    this.popupTimeout = setTimeout(() => {
      if (!event.relatedTarget || !(event.relatedTarget as HTMLElement).closest('.popup-container')) {
        this.showPopup = false;
        this.hoveredMovie = null;
        this.hoveredMovieIndex = -1;
      }
    }, 180);
  }
  
  onPopupEnter() {
    clearTimeout(this.popupTimeout); 
  }
  
  onPopupLeave() {
    this.showPopup = false; 
    setTimeout(() => {
      this.hoveredMovie = null; 
      this.hoveredMovieIndex = -1;
    }, 250);
  }

  onImageLoad() {
    this.imgLoading = false;
  }

  onImageError() {
    this.imgLoading = false;  
  }
  
  addVideoToWatchList(movie: any, event: Event) {
    event.stopPropagation();
    const userId = localStorage.getItem('userId');
    this.mycontentservice.addInWatchList(userId, movie.vidLibId).subscribe((result) => {
      if (result.status == 0) {
        movie.isInWatchList = true;
        this.showNotication('success', 'Successfully added to the watchlist');
      } else if (result.status == 1) {
        this.showNotication("warning", "Unable to add please try again");
      }    
    })
  }

  removeVideoFromWatchList(movie: any, event: Event) {
    event.stopPropagation();
    const userId = localStorage.getItem('userId');
    this.mycontentservice.removeFromWatchList(userId, movie.vidLibId).subscribe((result) => {
      if (result.status == 0) {
        movie.isInWatchList = false;
        this.showNotication('success', "Successfully removed from the watchlist");
      } else if (result.status == 1) {
        this.showNotication("warning", "Unable to remove please try again");
      }
    })
  }

  openPayments(hoveredMovie: any) {
    const route = this.whitelabelService.getPrefixedRoute('payment');
    this.router.navigate([route], {
      queryParams: {
        type: 'ppv',
        vidLibId: hoveredMovie?.vidLibId,
        movie: hoveredMovie?.title,
        durId: '2',
        planAmount: hoveredMovie.isChannelSubscribed ? hoveredMovie?.sub_value : hoveredMovie?.unSub_value,
        durationName: '2 Weeks',
      }
    });
  } 
  
  showPlansModal(hoveredMovie: any) {
    const route = this.whitelabelService.getPrefixedRoute('channelplans');
    this.router.navigate([route], {
      queryParams: {
        channelId: hoveredMovie?.channelId,
        channelName: hoveredMovie?.channel_desc,
        vidLibId: hoveredMovie?.vidLibId
      }
    })
  }
  
  openDetailView(video: any) {
    if(video.categoryId){
      localStorage.setItem('category', video.categoryId);
    }
    if(video?.series_id){
      const route = this.whitelabelService.getPrefixedRoute('shows', video.series_id);
      this.router.navigate([route]);
    }else{
      const route = this.whitelabelService.getPrefixedRoute('movies', video.title, video.vidLibId);
      this.router.navigate([route]);
    }
  }  

  openPlayerView(video: any){
    if(video.categoryId){
      localStorage.setItem('category', video.categoryId);
    }
    if(video?.series_id){
      const route = this.whitelabelService.getPrefixedRoute('shows', video.series_id, video.title, video.vidLibId, 'watch');
      this.router.navigate([route]);
    }else{
      const route = this.whitelabelService.getPrefixedRoute('movies', video.title, video.vidLibId, 'watch');
      this.router.navigate([route]);
    }
  }

  customOptions = {
    loop: false,
    mouseDrag: true,
    touchDrag: true,
    stagePadding: 0,
    pullDrag: true,
    dots: false,
    navSpeed: 300,
    nav: true,
    navText: ['<i class="fa fa-chevron-left"></i>', '<i class="fa fa-chevron-right"></i>'],
    responsive: {
      0: {
        items: 2,
        nav: false,
        margin: 10
      },
      400: {
        items: 2,
        nav: false,
        margin: 10
      },
      700: {
        items: 3,
        nav: false,
        margin: 10
      },
      900: {
        items: 3,
        margin: 12
      },
      1200: {
        items: 4,
        margin: 15
      },
      1600: {
        items: 5,
        margin: 15
      }
    }
  };

  showNotication(type: any, message: any) {
    this.toastrService.showNotification(
      type,
      '',
      message,
      3000
    );
  }
}